import Minze, { MinzeElement, Reactive, EventListeners, Attrs } from 'minze'
import { Routing } from '../../modules/routing'
import { EMAIL_REGEX, escapeHtml, unescapeHtml } from '../../modules/utils'
import MainModule from '../../modules/main'
import { Locale } from '../../modules/locale'
import { Font } from '../../constants'
Locale.getInstance().registerKeySet('fr', 'login', {
  login: 'Me connecter',
  email: 'Courriel',
  emailPlaceholder: 'Entrez votre adresse courriel',
  password: 'Mot de passe',
  passwordPlaceholder: 'Entrez votre mot de passe',
  forgotPassword: '<u>Mot de passe oublié?</u>',
  rememberMe: 'Se souvenir de moi',
  noAccountYet: "Pas encore de compte? <u>M'inscrire</u>",
  errors: {
    invalidEmail: 'Adresse courriel invalide',
    invalidPassword: 'Mot de passe invalide',
    general: 'Erreur de connexion',
    emailRequired: 'Ce champ est requis',
    passwordRequired: 'Ce champ est requis',
    noAccount: "Aucun compte n'est associé à cette adresse courriel",
  },
  providers: {
    google: 'Se connecter avec Google',
    facebook: 'Se connecter avec Facebook',
  },
})

Locale.getInstance().registerKeySet('en', 'login', {
  login: 'Sign in',
  email: 'E-mail',
  emailPlaceholder: 'Enter your email',
  password: 'Password',
  passwordPlaceholder: 'Enter your password',
  forgotPassword: '<u>Forgot your password?</u>',
  rememberMe: 'Remember me',
  noAccountYet: "Still don't have an account? <u>Sign up</u>",
  errors: {
    invalidEmail: 'Invalid email',
    invalidPassword: 'Invalid password',
    general: 'Login error',
    emailRequired: 'This field is required',
    passwordRequired: 'This field is required',
    noAccount: 'No account is associated with this email address',
  },
  providers: {
    google: 'Login with Google',
    facebook: 'Login with Facebook',
  },
})

export interface LvcLoginModalContent {
  email: string
  password: string
  redirectTo?: string
}

export class LvcLoginModalContent extends MinzeElement {
  attrs: Attrs = ['redirect-to']
  reactive: Reactive = [
    ['email', ''],
    ['password', ''],
  ]

  errors: any = {
    email: false,
    password: false,
    general: false,
  }

  onReady(): void | Promise<void> {
    this.rerender()
    this.email = localStorage.getItem('rememberedEmail') || ''
  }

  onLogin = async () => {
    for (const key in this.errors) {
      this.errors[key] = false
    }
    let error = null
    const emailElement: any = this.select('#email')
    const email = unescapeHtml(emailElement?.value)

    const passwordElement: any = this.select('#password')
    const password = unescapeHtml(passwordElement?.value)

    const rememberMeElement: any = this.select('[name="rememberMe"]')
    const rememberMe = rememberMeElement?.getAttribute('checked')
    const bRememberMe = rememberMe === 'true' || rememberMe === true

    const mainModule = MainModule.getInstance()
    if (!email) {
      this.errors.email = Locale.getKey('login.errors.emailRequired')
    } else if (!EMAIL_REGEX.test(email)) {
      this.errors.email = Locale.getKey('login.errors.invalidEmail')
    }

    if (!password) {
      this.errors.password = Locale.getKey('login.errors.passwordRequired')
    }

    let hasError = false

    for (const key in this.errors) {
      if (this.errors[key]) {
        hasError = true
      }
    }

    if (hasError) {
      this.rerender()
      return
    }

    if (email && password) {
      this.email = email
      this.password = password

      if (bRememberMe) {
        localStorage.setItem('rememberedEmail', email)
      } else {
        localStorage.removeItem('rememberedEmail')
      }

      error = await mainModule.getAuth().login(email, password, false, this.redirectTo)
    }

    if (error) {
      if (error.code === 'auth/user-not-found') {
        this.errors.email = Locale.getKey('login.errors.noAccount')
      } else if (error.code === 'auth/wrong-password') {
        this.errors.password = Locale.getKey('login.errors.invalidPassword')
      } else {
        this.errors.password = true
        this.errors.email = true
        this.errors.general = Locale.getKey('login.errors.general')
      }
    }

    this.rerender()
  }

  onSignupWithGoogle() {
    const mainModule = MainModule.getInstance()
    mainModule.getAuth().callGoogleSignIn()
  }

  onSignupWithFacebook() {
    const mainModule = MainModule.getInstance()
    mainModule.getAuth().callFacebookSignIn()
  }

  onEnterKey(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault()
      this.onLogin()
    }
  }

  html() {
    let rememberMe = !!localStorage.getItem('rememberedEmail') as boolean

    return `
      <div class="form">
        <lvc-box mt="8px" textAlign="${window.matchMedia('(max-width: 730px)').matches ? 'left' : 'center'}">
          <a class="no-account-yet" href="${MainModule.getInstance().getRouting().getRoute('signup')}">
            ${Locale.getKey('login.noAccountYet')}
          </a>
          </lvc-box>
        <lvc-box mt="24px">
          <lvc-input id="email" error="${this.errors.email}" name="email" placeholder="${Locale.getKey('login.emailPlaceholder')}" default-value="${escapeHtml(this.email)}" label="${Locale.getKey('login.email')}"></lvc-input>
        </lvc-box>
        <lvc-box mt="12px">
           <lvc-input id="password" error="${this.errors.password}" name="password" type="password" placeholder="${Locale.getKey('login.passwordPlaceholder')}" default-value="${escapeHtml(this.password)}" label="${Locale.getKey('login.password')}"></lvc-input>
        </lvc-box>
        <lvc-box mt="12px" class="forgot-password-link">
          <a href="${MainModule.getInstance().getRouting().getRoute('forgotPassword')}" class="forgot-password">${Locale.getKey('login.forgotPassword')}</a>
        </lvc-box>
        <lvc-box mt="${window.matchMedia('(max-width: 730px)').matches ? '16px' : '12px'}">
          <lvc-checkbox
            name="rememberMe"
            label="${Locale.getKey('login.rememberMe')}"
            default-value="${rememberMe}"
          />
        </lvc-box>
        ${
          this.errors.general
            ? `
          <lvc-box mt="12px" display="flex" width="100%" justifyContent="center">
            <lvc-error error="${this.errors.general}"></lvc-error>
          </lvc-box>
        `
            : ``
        }
        <lvc-box width="100%" mt="${window.matchMedia('(max-width: 730px)').matches ? '16px' : '12px'}">
          <lvc-button enable-hover-box-shadow="true" width="100%" class="button" type="submit">
            ${Locale.getKey('login.login')}
          </lvc-button>
        </lvc-box>      
        <lvc-box
          display="flex"
          width="100%"
          height="100%"
          mt="${window.matchMedia('(max-width: 730px)').matches ? '24px' : '32px'}"
        >
          <lvc-box borderBottom="1px solid #1E007D" flexGrow="1" mr="16px" mt="12px"></lvc-box>
          <lvc-text font-preset="mediumExtraBold">
            ${Locale.getKey('general.or')}
          </lvc-text>
          <lvc-box borderBottom="1px solid #1E007D" flexGrow="1" ml="16px" mt="12px"></lvc-box>
        </lvc-box>
       
        <lvc-box
          mt="${window.matchMedia('(max-width: 730px)').matches ? '24px' : '12px'}"
          display="${window.matchMedia('(max-width: 730px)').matches ? 'block' : 'flex'}"

          justifyContent="center"
        >
          <lvc-button
            width="${window.matchMedia('(max-width: 730px)').matches ? '100%' : 'fit-content'}" 
            class="google-sign-provider-button" 
            type="submit" 
            theme="transparent" 
            border="1px solid #1E007D"
            display="flex"
            justify-content="center"
            enable-hover-box-shadow="true"
          >
            <lvc-box display="flex" alignItems="center" columnGap="8px">
              ${Locale.getKey('login.providers.google')}
              <lvc-google-icon></lvc-google-icon>
            </lvc-box>
          </lvc-button>
        </lvc-box>      
        <lvc-box
          mt="${window.matchMedia('(max-width: 730px)').matches ? '8px' : '12px'}"
          display="${window.matchMedia('(max-width: 730px)').matches ? 'block' : 'flex'}"
          width="100%"
          justifyContent="center"
        >
          <lvc-button enable-hover-box-shadow="true" 
            width="${window.matchMedia('(max-width: 730px)').matches ? '100%' : 'fit-content'}" 
            class="facebook-sign-provider-button" 
            type="submit" 
            theme="transparent" 
            border="1px solid #1E007D"
            display="flex"
            justify-content="center"

          >
            <lvc-box display="flex" alignItems="center" columnGap="8px">
              ${Locale.getKey('login.providers.facebook')}
              <lvc-facebook-icon></lvc-facebook-icon>
            </lvc-box>
          </lvc-button>
        </lvc-box>  
      </div>
    `
  }

  css = () => `
    :host {
      
    }
    .switch-to-signup {
      cursor: pointer;
    }
    
    .forgot-password-link {
      display: flex;
      justify-content: flex-end;
   
    }

    a, a:visited {
      font-style: normal;
      font-weight: ${Font.weight.medium};
      font-size: 14px;
      text-decoration: none;
      color: #1E007D;
    }

    a>u:hover { 
      color: #FFF;
    }
  `

  eventListeners: EventListeners = [
    ['.button', 'click', this.onLogin.bind(this)],
    ['.google-sign-provider-button', 'click', this.onSignupWithGoogle.bind(this)],
    ['.facebook-sign-provider-button', 'click', this.onSignupWithFacebook.bind(this)],
    ['#email', 'keydown', (event: Event) => this.onEnterKey(event as KeyboardEvent)],
    ['#password', 'keydown', (event: Event) => this.onEnterKey(event as KeyboardEvent)],
  ]
}
